// FILE: src/lib/theme2026.js
// FILE VERSION: THEME2026-v1.1
// CHARLIE VERSION: v1.7.42
// CREATED: 2026-08-07
// UPDATED: 2026-09-07 (#2041)
//
// Real, standardized design system palette, per Shane's explicit spec
// 2026-08-07 -- not sampled pixels, a real locked palette meant to be
// reused consistently across the entire CHARLIE app rather than each
// component picking its own close-enough navy. This is the single
// real source of truth going forward; other files should import from
// here instead of hardcoding hex values for these roles.
//
// #2041 UPDATE (dark navy -> slate), real, per Stinger direct + A51's
// explicit scoping: "#334E68 is a starting reference, NOT authorization
// to scatter this exact hex throughout the application." Per that
// instruction, #334E68 is used here as ONE tier of a real tonal ramp
// (cardSidebar -- the natural "surface" reading for a mid slate),
// not pasted everywhere. The other three navy-family values are
// derived from it, preserving the SAME relative brightness
// relationship the original navy ramp had (bg darkest -> card ->
// hover -> icon-fill lightest), just re-based on slate instead of
// navy. Every one of the 44 files that already correctly import
// THEME_2026 picks up the real slate change automatically -- no
// per-file edits needed for those. brassAccent/warmGoldHighlight are
// UNCHANGED -- not in scope, #2041 is the navy family only.
//
// Real, confirmed-necessary addition, not scope creep: Viper's #2041
// audit flagged that semantic status colors (success/warning/error/
// attention/info) do not exist as tokens anywhere in the app despite
// being used ad hoc across components. Per A51's explicit requirement
// ("semantic colors must remain semantically meaningful, not
// flattened for visual consistency"), these are defined here now as
// their own distinct roles -- reusing the values already organically
// established in real live components (ChartWidget's PALETTE, the
// CRIMSON convention used for errors/urgent across the app) rather
// than inventing new arbitrary colors, so this documents existing
// practice rather than changing it. Pure addition -- nothing existing
// references these keys yet, so this carries zero regression risk.

// #WO-0492 UPDATE (2026-09-14), real, per A51's explicit ruling: the
// WO-0492 rebuild's approved visual palette (Charlie Navy #0B1F33,
// Ranch Navy #19364D, Deep Navy #102A43, Brass #C9972B, Warm Brass
// #D9AE45) is the new TARGET design system -- reconciled INTO this
// file rather than hardcoded alongside it as a competing system, per
// A51's explicit instruction not to create a third token system.
// Same keys, same 44 existing importers pick this up automatically --
// zero per-file changes required elsewhere.
//
// Role mapping (my judgment call, evidence-based -- flagged in the
// WO-0492 report as the specific reconciliation decision, not
// silently assumed): the WO gives 3 navy shades with no per-role
// assignment. Ordered by actual luminance: #0B1F33 (darkest) <
// #102A43 (mid) < #19364D (lightest of the 3). Mapped onto the
// EXISTING tonal-ramp structure (this file's own documented order:
// bg darkest -> card/sidebar -> hover -> icon-fill lightest):
//   primaryBg    = Charlie Navy #0B1F33 (darkest, main app bg)
//   cardSidebar  = Deep Navy   #102A43 ("the lapel" surface)
//   hoverState   = Ranch Navy  #19364D (lightest of the 3, appropriate
//                  for a hover step up from the resting sidebar tone)
//   graphiteIconFill -- WO gives no 4th navy value; PRESERVED
//                  unchanged (#4A6D89) per A51's "preserve load-bearing
//                  tokens unless a specific token must change" ruling.
// lapelGradient re-derived from the two new darkest tiers (no new hex
// invented) to keep the same top-lighter/bottom-darker relationship.
export const THEME_2026 = {
  primaryBg: '#0B1F33',       // Charlie Navy (WO-0492, was #23384A)
  accentSlate: '#334E68',     // the slate used for primary buttons and selected states across Lead Management, CRM and Settings (the value those screens already used)
  cardSidebar: '#102A43',     // Deep Navy (WO-0492, was #334E68)
  hoverState: '#19364D',      // Ranch Navy (WO-0492, was #3F5F7D)
  graphiteIconFill: '#4A6D89', // UNCHANGED -- no WO-0492 4th navy tier given, preserved per A51 ruling
  brassAccent: '#C9972B',     // Brass (WO-0492, was #C9A14A)
  warmGoldHighlight: '#D9AE45', // Warm Brass (WO-0492, was #E3BF67)
  wordmarkWhite: '#fff',      // WO-2912: C.H.A.R.L.I. letters on the loading screen (value as approved)
  wordmarkGrey: '#9CA3AF',    // WO-2912: WELL REPD ERP subtitle on the loading screen (value as approved)

  // Re-derived from the two new darkest navy tiers above -- same
  // top-lighter/bottom-darker relationship as before, no new hex.
  lapelGradient: 'linear-gradient(180deg, #102A43 0%, #0B1F33 100%)',

  // UNCHANGED -- WO-0492 does not specify new status hex values, and
  // A51's ruling preserves load-bearing tokens not specifically
  // required to change. These already satisfy the WO's "state colors
  // carry meaning" requirement structurally.
  statusSuccess: '#22c55e',
  statusWarning: '#f59e0b',
  statusError: '#99182c',
  statusAttention: '#b45309',
  statusInfo: '#0077cc',
};
